/**
 * PDF Export for Cyber Insurance Recommendation Tool
 * Generates comprehensive cyber insurance recommendation reports
 */

import type { IndustrySector, GeographicRegion, SecurityMaturityLevel, DataType } from '@/types/calculator';
import type jsPDF from 'jspdf';

// Extend jsPDF type to include autoTable plugin
interface jsPDFWithPlugin extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

interface InsuranceRecommendation {
  recommendedCoverageLimit: number;
  coverageRange: { min: number; max: number };
  estimatedPremium: { low: number; high: number };
  deductibleRange: { low: number; high: number };
  coverageTypes: {
    firstParty: CoverageItem[];
    thirdParty: CoverageItem[];
    endorsements: CoverageItem[];
  };
  premiumFactors: PremiumFactor[];
  readinessScore: number;
  readinessItems: ReadinessItem[];
  roi: {
    premiumCost: number;
    potentialPayout: number;
    outOfPocketWithout: number;
    outOfPocketWith: number;
    savingsRatio: number;
  };
  questionsForInsurer: QuestionForInsurer[];
}

interface CoverageItem {
  name: string;
  description: string;
  recommended: boolean;
  estimatedValue?: string;
}

interface PremiumFactor {
  factor: string;
  impact: 'increase' | 'decrease' | 'neutral';
  description: string;
  percentage?: number;
}

interface ReadinessItem {
  id: string;
  name: string;
  description: string;
  implemented: boolean;
  impact: 'critical' | 'high' | 'medium';
  premiumImpact: string;
}

interface QuestionForInsurer {
  category: string;
  question: string;
  whyItMatters: string;
}

interface PDFExportParams {
  recommendation: InsuranceRecommendation;
  inputs: {
    industry: IndustrySector;
    region: GeographicRegion;
    annualRevenue: number;
    recordCount: number;
    maturityLevel: SecurityMaturityLevel;
    hasIR: boolean;
    geographicScope: string;
    priorBreach: string;
    annualTransactionVolume: number;
    dataTypes: DataType[];
    readinessChecklist: Record<string, boolean>;
  };
  breachCostEstimate: number;
}

export async function generateCyberInsurancePDF(params: PDFExportParams) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const { recommendation, inputs, breachCostEstimate } = params;
  const doc = new jsPDF() as jsPDFWithPlugin;

  // Helper functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyShort = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${Math.round(value / 1000)}K`;
    }
    return `$${Math.round(value).toLocaleString()}`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatIndustry = (industry: string) => {
    return industry.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatRegion = (region: string) => {
    return region.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDataType = (dt: DataType): string => {
    const labels: Record<DataType, string> = {
      customer_pii: 'Personal Information (PII)',
      pci: 'Payment Card Data (PCI)',
      phi: 'Protected Health Info (PHI)',
      ssn_tax_id: 'SSN / Tax IDs',
      financial_records: 'Financial Records',
      ip_trade_secrets: 'Intellectual Property',
      employee_records: 'Employee Records',
      behavioral_data: 'Behavioral Data',
      credentials: 'Credentials'
    };
    return labels[dt] || dt;
  };

  const formatGeographicScope = (scope: string) => {
    const labels: Record<string, string> = {
      us_only: 'United States Only',
      north_america: 'North America',
      international: 'International'
    };
    return labels[scope] || scope;
  };

  // ============================================================================
  // TITLE PAGE
  // ============================================================================

  // Company branding
  doc.setFontSize(20);
  doc.setTextColor(37, 99, 235);
  doc.setFont('helvetica', 'bold');
  doc.text('INVENTIVE HQ', doc.internal.pageSize.width / 2, 50, { align: 'center' });

  // Main Title
  let titleYPos = 70;

  doc.setFontSize(28);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Cyber Insurance', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  titleYPos += 12;

  doc.setFontSize(28);
  doc.text('Recommendation Report', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  titleYPos += 12;

  doc.setFontSize(18);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.text('Coverage Analysis & Insurer Questions', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  // Report Details Box
  titleYPos += 30;

  doc.setFillColor(249, 250, 251);
  doc.roundedRect(30, titleYPos, doc.internal.pageSize.width - 60, 60, 3, 3, 'F');

  titleYPos += 12;

  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Organization:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatIndustry(inputs.industry) + ' Industry', 100, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Report Date:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), 100, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Annual Revenue:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(inputs.annualRevenue), 100, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Records at Risk:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatNumber(inputs.recordCount), 100, titleYPos);

  // Recommended Coverage Highlight
  titleYPos += 25;

  doc.setFillColor(37, 99, 235);
  doc.roundedRect(30, titleYPos, doc.internal.pageSize.width - 60, 35, 3, 3, 'F');

  titleYPos += 12;

  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.text('Recommended Coverage Limit', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  titleYPos += 10;

  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(recommendation.recommendedCoverageLimit), doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  // Footer on title page
  const footerY = doc.internal.pageSize.height - 30;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Based on your organization risk profile and industry benchmarks', doc.internal.pageSize.width / 2, footerY, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Confidential - For Internal Use Only', doc.internal.pageSize.width / 2, footerY + 6, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.setFont('helvetica', 'normal');
  doc.text('inventivehq.com  |  (866) 903-2097  |  info@inventivehq.com', doc.internal.pageSize.width / 2, footerY + 12, { align: 'center' });

  // Add new page for content
  doc.addPage();

  let yPos = 20;

  // ============================================================================
  // HEADER
  // ============================================================================

  doc.setFontSize(24);
  doc.setTextColor(37, 99, 235);
  doc.text('Cyber Insurance Recommendations', 20, yPos);

  yPos += 10;
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text('Generated by Inventive HQ | inventivehq.com', 20, yPos);

  yPos += 3;
  doc.setTextColor(156, 163, 175);
  doc.text(`Report Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, yPos);

  yPos += 15;

  // ============================================================================
  // EXECUTIVE SUMMARY
  // ============================================================================

  doc.setFillColor(37, 99, 235);
  doc.rect(20, yPos - 5, 170, 45, 'F');

  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('Coverage Summary', 25, yPos);

  yPos += 10;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Recommended Coverage: ${formatCurrency(recommendation.recommendedCoverageLimit)}`, 25, yPos);

  yPos += 7;
  doc.text(`Estimated Premium: ${formatCurrencyShort(recommendation.estimatedPremium.low)} - ${formatCurrencyShort(recommendation.estimatedPremium.high)}/year`, 25, yPos);

  yPos += 7;
  doc.text(`Readiness Score: ${recommendation.readinessScore}%`, 25, yPos);

  yPos += 7;
  doc.text(`Estimated Breach Cost: ${formatCurrency(breachCostEstimate)}`, 25, yPos);

  yPos += 20;

  // ============================================================================
  // ORGANIZATION PROFILE
  // ============================================================================

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Organization Profile', 20, yPos);

  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Attribute', 'Value']],
    body: [
      ['Industry', formatIndustry(inputs.industry)],
      ['Geographic Region', formatRegion(inputs.region)],
      ['Annual Revenue', formatCurrency(inputs.annualRevenue)],
      ['Records at Risk', formatNumber(inputs.recordCount)],
      ['Annual Transactions', formatNumber(inputs.annualTransactionVolume)],
      ['Geographic Scope', formatGeographicScope(inputs.geographicScope)],
      ['Security Maturity', inputs.maturityLevel.charAt(0).toUpperCase() + inputs.maturityLevel.slice(1)],
      ['Incident Response Plan', inputs.hasIR ? 'Yes' : 'No'],
      ['Data Types', inputs.dataTypes.map(dt => formatDataType(dt)).join(', ')]
    ],
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 100 }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Check if we need a new page
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  // ============================================================================
  // COVERAGE RECOMMENDATIONS
  // ============================================================================

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Coverage Recommendations', 20, yPos);

  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value']],
    body: [
      ['Recommended Coverage', formatCurrency(recommendation.recommendedCoverageLimit)],
      ['Coverage Range', `${formatCurrencyShort(recommendation.coverageRange.min)} - ${formatCurrencyShort(recommendation.coverageRange.max)}`],
      ['Estimated Annual Premium', `${formatCurrencyShort(recommendation.estimatedPremium.low)} - ${formatCurrencyShort(recommendation.estimatedPremium.high)}`],
      ['Recommended Deductible', `${formatCurrencyShort(recommendation.deductibleRange.low)} - ${formatCurrencyShort(recommendation.deductibleRange.high)}`],
      ['Insurance ROI', `${recommendation.roi.savingsRatio.toFixed(1)}x potential savings`]
    ],
    theme: 'grid',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 80, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // FIRST-PARTY COVERAGE
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('First-Party Coverage (Your Losses)', 20, yPos);

  yPos += 8;

  const firstPartyData = recommendation.coverageTypes.firstParty.map(item => [
    item.name,
    item.description,
    item.recommended ? 'Recommended' : 'Optional',
    item.estimatedValue || '-'
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Coverage', 'Description', 'Status', 'Est. Value']],
    body: firstPartyData,
    theme: 'striped',
    headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 60 },
      2: { cellWidth: 30, halign: 'center' },
      3: { cellWidth: 45, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // ============================================================================
  // THIRD-PARTY COVERAGE
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Third-Party Coverage (Liability)', 20, yPos);

  yPos += 8;

  const thirdPartyData = recommendation.coverageTypes.thirdParty.map(item => [
    item.name,
    item.description,
    item.recommended ? 'Recommended' : 'Optional',
    item.estimatedValue || '-'
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Coverage', 'Description', 'Status', 'Est. Value']],
    body: thirdPartyData,
    theme: 'striped',
    headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 60 },
      2: { cellWidth: 30, halign: 'center' },
      3: { cellWidth: 45, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // ============================================================================
  // ENDORSEMENTS
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Optional Endorsements', 20, yPos);

  yPos += 8;

  const endorsementsData = recommendation.coverageTypes.endorsements.map(item => [
    item.name,
    item.description,
    item.recommended ? 'Consider' : 'Optional',
    item.estimatedValue || '-'
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Coverage', 'Description', 'Status', 'Est. Value']],
    body: endorsementsData,
    theme: 'striped',
    headStyles: { fillColor: [249, 115, 22], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 60 },
      2: { cellWidth: 30, halign: 'center' },
      3: { cellWidth: 45, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // PREMIUM FACTORS
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Factors Affecting Your Premium', 20, yPos);

  yPos += 8;

  const premiumData = recommendation.premiumFactors.map(factor => [
    factor.factor,
    factor.description,
    factor.impact === 'increase' ? `+${factor.percentage}%` : factor.impact === 'decrease' ? `-${factor.percentage}%` : '0%',
    factor.impact === 'increase' ? 'Increases' : factor.impact === 'decrease' ? 'Decreases' : 'Neutral'
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Factor', 'Description', 'Impact', 'Effect']],
    body: premiumData,
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 70 },
      2: { cellWidth: 25, halign: 'center' },
      3: { cellWidth: 30, halign: 'center' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // READINESS ASSESSMENT
  // ============================================================================

  if (yPos > 180) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text(`Insurance Readiness Assessment - Score: ${recommendation.readinessScore}%`, 20, yPos);

  yPos += 8;

  const readinessData = recommendation.readinessItems.map(item => [
    item.name,
    item.implemented ? 'Yes' : 'No',
    item.impact.toUpperCase(),
    item.premiumImpact
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Security Control', 'Implemented', 'Priority', 'Premium Impact']],
    body: readinessData,
    theme: 'striped',
    headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 25, halign: 'center' },
      3: { cellWidth: 70 }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // QUESTIONS FOR INSURERS
  // ============================================================================

  doc.addPage();
  yPos = 20;

  doc.setFontSize(18);
  doc.setTextColor(37, 99, 235);
  doc.setFont('helvetica', 'bold');
  doc.text('Questions to Ask Your Insurer', 20, yPos);

  yPos += 10;

  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  const introText = doc.splitTextToSize('Use these questions when shopping for cyber insurance. They are tailored to your organization\'s risk profile.', 170);
  doc.text(introText, 20, yPos);

  yPos += (introText.length * 5) + 10;

  // Group questions by category
  const categories = [...new Set(recommendation.questionsForInsurer.map(q => q.category))];

  categories.forEach((category) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text(category, 20, yPos);

    yPos += 8;

    const categoryQuestions = recommendation.questionsForInsurer.filter(q => q.category === category);

    categoryQuestions.forEach((q, index) => {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'bold');
      const questionText = doc.splitTextToSize(`${index + 1}. ${q.question}`, 165);
      doc.text(questionText, 25, yPos);

      yPos += (questionText.length * 5) + 2;

      doc.setFontSize(9);
      doc.setTextColor(75, 85, 99);
      doc.setFont('helvetica', 'italic');
      const whyText = doc.splitTextToSize(`Why: ${q.whyItMatters}`, 160);
      doc.text(whyText, 28, yPos);

      yPos += (whyText.length * 4) + 6;
    });

    yPos += 5;
  });

  // ============================================================================
  // ABOUT INVENTIVE HQ
  // ============================================================================

  doc.addPage();
  yPos = 20;

  const pageWidth = doc.internal.pageSize.width;

  doc.setFillColor(37, 99, 235);
  doc.rect(20, yPos - 5, 170, 12, 'F');

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('About Inventive HQ', 25, yPos + 3);

  yPos += 20;

  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'normal');

  const aboutText = [
    'Inventive HQ is a leading cybersecurity and IT services provider specializing in protecting businesses',
    'from cyber threats. We help organizations understand their risk exposure, improve their security posture,',
    'and navigate the cyber insurance landscape with confidence.'
  ];

  aboutText.forEach(line => {
    doc.text(line, 20, yPos);
    yPos += 5;
  });

  yPos += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('How We Can Help', 20, yPos);

  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const services = [
    'Security assessments to improve your insurance readiness score',
    'Virtual CISO services for security program oversight',
    'Incident response planning and testing',
    'Compliance gap assessments (HIPAA, PCI DSS, SOC 2)',
    'Security awareness training for your team'
  ];

  services.forEach(service => {
    doc.setFillColor(37, 99, 235);
    doc.circle(24, yPos - 1, 1, 'F');
    doc.text(service, 30, yPos);
    yPos += 6;
  });

  yPos += 20;

  // Contact CTA
  doc.setFillColor(59, 130, 246);
  doc.roundedRect(20, yPos, 170, 40, 3, 3, 'F');

  yPos += 12;

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('Need Help With Cyber Insurance?', pageWidth / 2, yPos, { align: 'center' });

  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Contact us for a free consultation on improving your security posture', pageWidth / 2, yPos, { align: 'center' });

  yPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('inventivehq.com/contact  |  (866) 903-2097', pageWidth / 2, yPos, { align: 'center' });

  // ============================================================================
  // FOOTER ON EVERY PAGE
  // ============================================================================

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      'Generated by Inventive HQ Cyber Insurance Recommendation Tool | inventivehq.com',
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 6,
      { align: 'center' }
    );
  }

  // ============================================================================
  // SAVE THE PDF
  // ============================================================================

  const fileName = `Cyber-Insurance-Recommendations-${formatIndustry(inputs.industry).replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
